import type { TFunction } from 'i18next';

import type { IBulkEdit, IContactBulkJob } from '../types';

/**
 * What a background bulk job is doing, in the user's terms. The server only knows
 * DELETE / EDIT / SEGMENT_*; the caller that started the job knows which edit it was
 * (opt-out, tags, …). A job resumed after a reload falls back to its server action.
 */
export type BulkJobKind =
    | 'delete'
    | 'optOut'
    | 'optIn'
    | 'addTags'
    | 'removeTags'
    | 'edit'
    | 'segmentCreate'
    | 'segmentAdd'
    | 'segmentRemove';

export function bulkJobKindFromEdit(data: IBulkEdit): BulkJobKind {
    if (data.isOptedOut === true) return 'optOut';
    if (data.isOptedOut === false) return 'optIn';
    if (data.addTagIds?.length && !data.removeTagIds?.length) return 'addTags';
    if (data.removeTagIds?.length && !data.addTagIds?.length) return 'removeTags';
    return 'edit';
}

export function bulkJobKindFromAction(action: IContactBulkJob['action']): BulkJobKind {
    if (action === 'DELETE') return 'delete';
    if (action === 'SEGMENT_ADD') return 'segmentAdd';
    if (action === 'SEGMENT_REMOVE') return 'segmentRemove';
    return 'edit';
}

/** Toast title while the job runs. */
export function bulkJobRunningTitle(kind: BulkJobKind, t: TFunction): string {
    const titles: Record<BulkJobKind, string> = {
        delete: t('contacts.bulkJob.title.delete', 'Deleting contacts'),
        optOut: t('contacts.bulkJob.title.optOut', 'Opting out contacts'),
        optIn: t('contacts.bulkJob.title.optIn', 'Opting in contacts'),
        addTags: t('contacts.bulkJob.title.addTags', 'Adding tags'),
        removeTags: t('contacts.bulkJob.title.removeTags', 'Removing tags'),
        edit: t('contacts.bulkJob.title.edit', 'Updating contacts'),
        segmentCreate: t(
            'contacts.bulkJob.title.segmentCreate',
            'Segment created — adding contacts',
        ),
        segmentAdd: t('contacts.bulkJob.title.segmentAdd', 'Adding contacts to segment'),
        segmentRemove: t('contacts.bulkJob.title.segmentRemove', 'Removing contacts from segment'),
    };
    return titles[kind];
}

/**
 * The finished line: real counts, never "of total" (the matching set can change while
 * the job runs). Opt-in says why contacts were skipped — a contact who texted STOP is
 * never re-subscribed by a bulk action, only by their own START.
 */
export function bulkJobSummary(kind: BulkJobKind, job: IContactBulkJob, t: TFunction): string {
    const counts = {
        affected: job.affected.toLocaleString(),
        skipped: job.skipped.toLocaleString(),
    };
    switch (kind) {
        case 'delete':
            return t(
                'contacts.bulkJob.done.delete',
                'Deleted {{affected}} · skipped {{skipped}}',
                counts,
            );
        case 'optOut':
            return t(
                'contacts.bulkJob.done.optOut',
                'Opted out {{affected}} · {{skipped}} were already opted out',
                counts,
            );
        case 'optIn':
            return t(
                'contacts.bulkJob.done.optIn',
                'Opted in {{affected}} · skipped {{skipped}} (already opted in, or they texted STOP — only their START re-subscribes them)',
                counts,
            );
        case 'addTags':
        case 'removeTags':
            return t('contacts.bulkJob.done.tags', 'Updated tags on {{affected}} contacts', counts);
        case 'segmentCreate':
        case 'segmentAdd':
            return t(
                'contacts.bulkJob.done.segmentAdd',
                'Added {{affected}} to the segment · skipped {{skipped}}',
                counts,
            );
        case 'segmentRemove':
            return t(
                'contacts.bulkJob.done.segmentRemove',
                'Removed {{affected}} from the segment · skipped {{skipped}}',
                counts,
            );
        default:
            return t(
                'contacts.bulkJob.summary',
                'Updated {{affected}} · skipped {{skipped}}',
                counts,
            );
    }
}
